import { Button, Grid, LinearProgress, makeStyles, Tab, Tabs, Typography } from "@material-ui/core";
import {
  AddCircleOutline as AddCircleOutlineIcon,
  CameraAltOutlined as CameraAltOutlinedIcon,
  Edit as EditIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Launch as LaunchIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  YouTube as YoutubeIcon
} from "@material-ui/icons";
import { useQuery } from "@redux-requests/react";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { BaseLayout, HeadBar, VideoDialog } from "../components";
import DocumentGrid from "../components/DocumentGrid";
import EditImageDialog from "../components/EditImageDialog";
import PdfDialog from "../components/PdfDialog";
import RecordingGrid from "../components/RecordingGrid";
import { types } from "../ducks";
import { getExhibitor } from "../ducks/actions";
import {
  CompanyProfileTab,
  DocumentDialog,
  EditExhibitorDialog,
  SponsoredSessionsTab
} from "../features/exhibitors";
import RecordingDialog from "../features/exhibitors/RecordingDialog";
import useExhibitorOwner from "../hooks/useExhibitorOwner";

const useStyles = makeStyles(theme => ({
  root: {
    "& > *": {
      marginBottom: theme.spacing(3)
    }
  },
  coverContainer: {
    position: "relative"
  },
  cover: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3],
    width: "100%",
    backgroundColor: theme.palette.primary.light
  },
  coverEmpty: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3],
    width: "100%",
    height: theme.spacing(32),
    objectFit: "cover",
    backgroundColor: theme.palette.primary.light
  },
  logoContainer: {
    position: "relative",
    width: "160px"
  },
  logo: {
    marginTop: -theme.spacing(10),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3],
    height: theme.spacing(20),
    width: theme.spacing(20),
    objectFit: "contain",
    backgroundColor: "white",
    padding: 4
  },
  exhibitorInfo: {
    [theme.breakpoints.up(767)]: {
      paddingLeft: theme.spacing(3)
    }
  },
  headbarToolbar: {
    [theme.breakpoints.down(767)]: {
      textAlign: "center"
    },
    marginBottom: theme.spacing(1)
  },
  rightToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    "& > *": {
      marginBottom: theme.spacing(1)
    }
  },
  socialButtonBar: {
    display: "flex",
    "& > *": {
      marginRight: theme.spacing(1)
    }
  },
  iconButton: {
    padding: 0
  },
  textFormatted: {
    whiteSpace: "pre-line"
  },
  cameraButton: {
    borderRadius: "0.25rem",
    height: "2rem",
    width: "2rem",
    padding: "0.25rem",
    background: "rgba(0, 0, 0, 0.3)",
    position: "absolute",
    bottom: "1rem",
    right: "1rem",
    cursor: "pointer"
  }
}));

const ExhibitorsPage = () => {
  const classes = useStyles();

  const intl = useIntl();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [editExhibitorDialog, setEditExhibitorDialog] = useState(false);
  const [editCoverDialog, setEditCoverDialog] = useState(false);
  const [editLogoDialog, setEditLogoDialog] = useState(false);
  const [tab, setTab] = useState("companyProfile");
  const [selectedRecording, setSelectedRecording] = useState();
  const [selectedDocument, setSelectedDocument] = useState();

  const onTabChange = (e, newTab) => {
    setTab(newTab);
  };

  useEffect(() => {
    dispatch(getExhibitor({ id }));
  }, [dispatch, id]);

  const {
    data: { data: exhibitor },
    loading
  } = useQuery({ type: types.GET_EXHIBITOR, defaultData: { data: {} } });

  const isExhibitorOwner = useExhibitorOwner({ exhibitorId: id });

  return (
    <BaseLayout className={classes.root}>
      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <div className={classes.coverContainer}>
            {exhibitor.cover ? (
              <img src={exhibitor.cover} alt={exhibitor.name} className={classes.cover} />
            ) : (
              <div className={classes.coverEmpty} />
            )}
            {isExhibitorOwner && (
              <CameraAltOutlinedIcon
                className={classes.cameraButton}
                onClick={() => {
                  setEditCoverDialog(true);
                }}
              />
            )}
          </div>
          <HeadBar>
            <Grid container>
              <Grid item xs={12} sm={6} md={8} lg={9} className={classes.headbarToolbar}>
                <div className={classes.exhibitorInfo}>
                  <div className={classes.logoContainer}>
                    {exhibitor.logo ? (
                      <img src={exhibitor.logo} alt={exhibitor.name} className={classes.logo} />
                    ) : (
                      <div className={classes.logo} />
                    )}
                    {isExhibitorOwner && (
                      <CameraAltOutlinedIcon
                        className={classes.cameraButton}
                        onClick={() => {
                          setEditLogoDialog(true);
                        }}
                      />
                    )}
                  </div>
                  <Typography variant="h4">
                    <strong>{exhibitor.name}</strong>
                  </Typography>
                  <Typography variant="body1" paragraph className={classes.textFormatted}>
                    {exhibitor.description}
                  </Typography>
                  <div className={classes.socialButtonBar}>
                    {exhibitor.websiteUrl && (
                      <Button
                        endIcon={<LaunchIcon />}
                        href={exhibitor.websiteUrl}
                        target="_blank"
                        variant="outlined"
                        color="primary"
                        size="large"
                      >
                        {intl.formatMessage({
                          id: "Pages.ExhibitorPage.Website",
                          defaultMessage: "Website"
                        })}
                      </Button>
                    )}
                    {exhibitor.linkedinUrl && (
                      <Button
                        className={classes.iconButton}
                        href={exhibitor.linkedinUrl}
                        target="_blank"
                        variant="outlined"
                        color="primary"
                        size="medium"
                      >
                        <LinkedInIcon />
                      </Button>
                    )}
                    {exhibitor.facebookUrl && (
                      <Button
                        className={classes.iconButton}
                        href={exhibitor.facebookUrl}
                        target="_blank"
                        variant="outlined"
                        color="primary"
                        size="medium"
                      >
                        <FacebookIcon />
                      </Button>
                    )}
                    {exhibitor.instagramUrl && (
                      <Button
                        className={classes.iconButton}
                        href={exhibitor.instagramUrl}
                        target="_blank"
                        variant="outlined"
                        color="primary"
                        size="medium"
                      >
                        <InstagramIcon />
                      </Button>
                    )}
                    {exhibitor.twitterUrl && (
                      <Button
                        className={classes.iconButton}
                        href={exhibitor.twitterUrl}
                        target="_blank"
                        variant="outlined"
                        color="primary"
                        size="medium"
                      >
                        <TwitterIcon />
                      </Button>
                    )}
                    {exhibitor.youtubeUrl && (
                      <Button
                        className={classes.iconButton}
                        href={exhibitor.youtubeUrl}
                        target="_blank"
                        variant="outlined"
                        color="primary"
                        size="medium"
                      >
                        <YoutubeIcon />
                      </Button>
                    )}
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} className={classes.rightToolbar}>
                {isExhibitorOwner && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setEditExhibitorDialog(true);
                    }}
                  >
                    <EditIcon />
                    &nbsp;&nbsp;&nbsp;
                    {intl.formatMessage({
                      id: "Pages.ExhibitorPage.EditProfileInfo",
                      defaultMessage: "Edit Profile Info"
                    })}
                  </Button>
                )}
              </Grid>
            </Grid>
          </HeadBar>
          <Tabs value={tab} onChange={onTabChange} indicatorColor="primary" variant="scrollable">
            <Tab
              value="companyProfile"
              label={intl.formatMessage({
                id: "Pages.ExhibitorPage.CompanyProfile",
                defaultMessage: "Company Profile"
              })}
            />
            {!isEmpty(exhibitor.sessions) && (
              <Tab
                value="sponsoredSessions"
                label={intl.formatMessage({
                  id: "Pages.ExhibitorPage.SponsoredSessions",
                  defaultMessage: "Sponsored Sessions"
                })}
              />
            )}
            {(isExhibitorOwner || Boolean(exhibitor.documents?.length)) && (
              <Tab
                value="documents"
                label={intl.formatMessage({
                  id: "Pages.ExhibitorPage.Documents",
                  defaultMessage: "Documents"
                })}
              />
            )}
            {(isExhibitorOwner || Boolean(exhibitor.recordings?.length)) && (
              <Tab
                value="videos"
                label={intl.formatMessage({
                  id: "Pages.ExhibitorPage.Videos",
                  defaultMessage: "Videos"
                })}
              />
            )}
          </Tabs>
          {tab === "companyProfile" && <CompanyProfileTab exhibitor={exhibitor} />}
          {tab === "sponsoredSessions" && (
            <SponsoredSessionsTab exhibitorSessions={exhibitor.sessions} />
          )}
          {tab === "documents" && (
            <DocumentDialog
              exhibitorId={id}
              render={({ onOpen }) => (
                <>
                  {isExhibitorOwner && (
                    <Button onClick={() => onOpen({ id: null })} color="primary" variant="outlined">
                      <AddCircleOutlineIcon />
                      &nbsp;&nbsp;&nbsp;
                      {intl.formatMessage({
                        id: "Pages.ExhibitorPage.AddDocument",
                        defaultMessage: "Add Document"
                      })}
                    </Button>
                  )}
                  <DocumentGrid
                    loading={loading}
                    documents={exhibitor.documents}
                    onDocumentClick={setSelectedDocument}
                    isEditable
                    onEdit={({ id }) => {
                      onOpen({ id });
                    }}
                  />
                </>
              )}
            />
          )}
          {tab === "videos" && (
            <RecordingDialog
              exhibitorId={id}
              render={({ onOpen }) => (
                <>
                  {isExhibitorOwner && (
                    <Button onClick={() => onOpen({ id: null })} color="primary" variant="outlined">
                      <AddCircleOutlineIcon />
                      &nbsp;&nbsp;&nbsp;
                      {intl.formatMessage({
                        id: "Pages.ExhibitorPage.AddRecording",
                        defaultMessage: "Add Video"
                      })}
                    </Button>
                  )}
                  <RecordingGrid
                    loading={loading}
                    recordings={exhibitor.recordings}
                    onRecordingClick={setSelectedRecording}
                    isEditable
                    onEdit={({ id }) => {
                      onOpen({ id });
                    }}
                  />
                </>
              )}
            />
          )}
        </>
      )}
      {editExhibitorDialog && (
        <EditExhibitorDialog
          id={id}
          exhibitor={exhibitor}
          onClose={() => {
            setEditExhibitorDialog(false);
          }}
        />
      )}
      {selectedRecording && (
        <VideoDialog
          videoUrl={selectedRecording.videoUrl}
          title={selectedRecording.title}
          description={selectedRecording.description}
          onClose={() => {
            setSelectedRecording(null);
          }}
        />
      )}
      {selectedDocument && (
        <PdfDialog
          document={selectedDocument.pdf}
          title={selectedDocument.title}
          description={selectedDocument.description}
          onClose={() => {
            setSelectedDocument(null);
          }}
        />
      )}
      {editCoverDialog && (
        <EditImageDialog
          parentId={id}
          parentType="exhibitor"
          imageType="cover"
          imageUrl={exhibitor.cover}
          onClose={() => {
            setEditCoverDialog(null);
          }}
        />
      )}
      {editLogoDialog && (
        <EditImageDialog
          parentId={id}
          parentType="exhibitor"
          imageType="logo"
          imageUrl={exhibitor.logo}
          onClose={() => {
            setEditLogoDialog(null);
          }}
        />
      )}
    </BaseLayout>
  );
};

export default ExhibitorsPage;
